import React from 'react';

/**
 * Shared primitives for v3 bath-fixture elevation schematics.
 *
 * Mirrors `ApplianceFrame.jsx` (v2). Coordinate convention is the same
 * meter-space viewBox used by the caller (`RenderWallElevation.js`):
 *   viewBox="0 0 widthMeters heightMeters"
 *   preserveAspectRatio="none"
 *
 * Bath fixtures are visually smaller silhouettes than kitchen
 * appliances — faucets and showerheads especially. To stay legible at
 * typical elevation scale these primitives lean on stroke weight (the
 * caller passes `strokeWidth` already inflated for capture mode) rather
 * than fine internal detail. Open silhouettes (no outer rect) are
 * common for plumbing fixtures, so callers compose primitives freely
 * instead of always starting from a frame rect.
 */

const INCHES_TO_METERS = 0.0254;
export const INCH_M = INCHES_TO_METERS;

/**
 * Outer SVG shell for a bath fixture. Unlike `ApplianceShell`, this
 * does NOT draw a frame rect — most bath fixtures (toilets, faucets,
 * showerheads) read better as open silhouettes than as boxed
 * appliances. Components that DO want a rect (alcove tub, mirror
 * frame on a sink, etc.) draw it explicitly via `<Rect />` or
 * directly inside children.
 *
 * `preserveAspectRatio="none"` matches the v1/v2 caller contract: the
 * schematic stretches to fill its absolutely-sized container, since
 * the container is already in correct elevation pixel dimensions.
 */
export function BathFixtureShell({ width, height, children }) {
  return (
    <svg
      viewBox={`0 0 ${width} ${height}`}
      preserveAspectRatio="none"
      style={{ width: '100%', height: '100%', overflow: 'visible' }}
    >
      {children}
    </svg>
  );
}

export function Rect({
  x,
  y,
  width,
  height,
  strokeColor,
  strokeWidth,
  rx = 0,
  ry = 0,
}) {
  return (
    <rect
      x={x}
      y={y}
      width={width}
      height={height}
      rx={rx}
      ry={ry}
      fill="none"
      stroke={strokeColor}
      strokeWidth={strokeWidth}
      strokeLinecap="round"
      strokeLinejoin="round"
      vectorEffect="non-scaling-stroke"
    />
  );
}

export function Line({ x1, y1, x2, y2, strokeColor, strokeWidth }) {
  return (
    <line
      x1={x1}
      y1={y1}
      x2={x2}
      y2={y2}
      stroke={strokeColor}
      strokeWidth={strokeWidth}
      strokeLinecap="round"
      vectorEffect="non-scaling-stroke"
    />
  );
}

export function Circle({ cx, cy, r, strokeColor, strokeWidth, fill = 'none' }) {
  return (
    <circle
      cx={cx}
      cy={cy}
      r={r}
      fill={fill}
      stroke={strokeColor}
      strokeWidth={strokeWidth}
      vectorEffect="non-scaling-stroke"
    />
  );
}

/**
 * Quadratic-bezier curve helper — used for the spout arcs on faucets
 * and the curved profile of a clawfoot tub.
 */
export function Curve({ d, strokeColor, strokeWidth, fill = 'none' }) {
  return (
    <path
      d={d}
      fill={fill}
      stroke={strokeColor}
      strokeWidth={strokeWidth}
      strokeLinecap="round"
      strokeLinejoin="round"
      vectorEffect="non-scaling-stroke"
    />
  );
}

/**
 * Floor line — single horizontal stroke flush with the bottom edge.
 * Bath fixtures use this to ground freestanding silhouettes (clawfoot,
 * pedestal sink). Drawn slightly inset so it doesn't get clipped.
 */
export function FloorBaseline({ width, height, strokeColor, strokeWidth }) {
  const y = height - Math.max(0.002, height * 0.01);
  return (
    <Line
      x1={0}
      y1={y}
      x2={width}
      y2={y}
      strokeColor={strokeColor}
      strokeWidth={strokeWidth}
    />
  );
}
